/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useFeatureFlag } from '@/features/globalConfig';

/**
 * Determines if Certona add-to-cart CTAs should be enabled based on current page and feature flags.
 *
 * Post-ACES: Uses single Monetate flag (`enableCertonaAddToCartCTA`) for all pages.
 *
 * Pre-ACES: Falls back to page-specific feature flags for backward compatibility.
 * @param {boolean} [options.ignorePageSpecificFlag=false] - Bypass page-specific logic and use global flag
 * @returns {boolean} True if Certona CTAs should be displayed
 */
export const useCertonaAddToCartFlag = ({
  ignorePageSpecificFlag = false,
}: {
  ignorePageSpecificFlag?: boolean;
} = {}): boolean => {
  // Post-ACES: all Certona add-to-cart CTAs are controlled by a single flag
  const { acesEnabled } = useAcesFlags();
  const isKiboCertonaAddToCartEnabled = useFeatureFlag('IS_CERTONA_ADD_TO_CART_ENABLED') === 'true';

  // Parts Pro: most Certona add-to-cart CTAs are controlled by page-specific flags
  const router = useLegacyRouter();
  const isPDP = router?.pathname === '/products/[...slug]';
  const isHome = router?.pathname === '/';
  const isShelf = router?.pathname === '/[...seoUrlPath]';
  const isSearchPage = router?.pathname?.includes('search');

  const isPdpCertonaAddToCartEnabled =
    useFeatureFlag('IS_PDP_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';
  const isHomePageCertonaAddToCartEnabled =
    useFeatureFlag('IS_HOME_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';
  const isShelfPageCertonaAddToCartEnabled =
    useFeatureFlag('IS_SHELF_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';
  const isSearchPageCertonaAddToCartEnabled =
    useFeatureFlag('IS_SEARCH_PAGE_CERTONA_ADD_TO_CART_ENABLED') === 'true';

  const isBccCertonaAddToCartCTAEnabled =
    useFeatureFlag('IS_CERTONA_ADD_TO_CART_LINK_ENABLED') === 'true';

  switch (true) {
    case acesEnabled:
      return isKiboCertonaAddToCartEnabled;
    case ignorePageSpecificFlag:
      return isBccCertonaAddToCartCTAEnabled;
    case isPDP:
      return isPdpCertonaAddToCartEnabled;
    case isHome:
      return isHomePageCertonaAddToCartEnabled;
    case isShelf:
      return isShelfPageCertonaAddToCartEnabled;
    case isSearchPage:
      return isSearchPageCertonaAddToCartEnabled;
    default:
      return false;
  }
};
